import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faDash } from '@fortawesome/pro-solid-svg-icons';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Gain } from '@sorare/core/src/components/scoring/Gain';
import { ProjectionScore } from '@sorare/core/src/components/scoring/ProjectionScore';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { Cell } from 'components/searchPlayerTable/SearchTable/ui';
import { PROJECTION_WITH_DIFFERENCE_BY_SPORT } from 'lib/projection';

import type { ProjectionCell_anyPlayer } from './__generated__/index.graphql';

type Props = {
  anyPlayer: ProjectionCell_anyPlayer;
  so5FixtureEvent: So5FixtureEvent;
};

export const ProjectionCell = ({ anyPlayer, so5FixtureEvent }: Props) => {
  const {
    flags: { useHandleL10Gain = false },
  } = useFeatureFlags();

  const { up: laptopOrAbove } = useScreenSize('laptop');

  const { sport } = anyPlayer;
  const withDifference =
    useHandleL10Gain && PROJECTION_WITH_DIFFERENCE_BY_SPORT[sport];
  const { projectedScore, difference } = (() => {
    switch (so5FixtureEvent) {
      case So5FixtureEvent.DAILY:
        return {
          projectedScore: anyPlayer.nextDailyFixtureProjectedScore,
          difference: anyPlayer.differenceLastTenAndDailyProjection,
        };

      case So5FixtureEvent.CLASSIC:
      default:
        return {
          projectedScore: anyPlayer.nextClassicFixtureProjectedScore,
          difference: anyPlayer.differenceLastTenAndClassicProjection,
        };
    }
  })();

  return (
    <>
      <Cell>
        {projectedScore === null ? (
          <FontAwesomeIcon icon={faDash} color="var(--c-nd-500)" />
        ) : (
          <ProjectionScore
            size={laptopOrAbove ? 4 : 3}
            projectedScore={projectedScore}
            so5FixtureEvent={so5FixtureEvent}
          />
        )}
      </Cell>
      {!!withDifference && (
        <Cell>
          {difference === null ? (
            <FontAwesomeIcon icon={faDash} color="var(--c-nd-500)" />
          ) : (
            <Gain value={difference} size={laptopOrAbove ? 4 : 3} />
          )}
        </Cell>
      )}
    </>
  );
};

ProjectionCell.fragments = {
  anyPlayer: gql`
    fragment ProjectionCell_anyPlayer on AnyPlayerInterface {
      slug
      sport
      nextDailyFixtureProjectedScore
      nextClassicFixtureProjectedScore
      differenceLastTenAndDailyProjection
      differenceLastTenAndClassicProjection
    }
  ` as TypedDocumentNode<ProjectionCell_anyPlayer>,
};
